import { API_BASE } from '../utils'
import type {
  GameCatalogResponse,
  PlayerGuessHintType,
  PlayerGuessSessionResponse,
} from '../types/games'

export class GamesApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'GamesApiError'
    this.status = status
  }
}

async function throwApiError(res: Response): Promise<never> {
  let detail = String(res.status)
  try {
    const body: unknown = await res.json()
    if (typeof body === 'object' && body !== null && typeof (body as { detail?: unknown }).detail === 'string') {
      detail = (body as { detail: string }).detail
    }
  } catch {
    // Body wasn't JSON — fall back to the status-based message above.
  }
  throw new GamesApiError(detail, res.status)
}

function authHeaders(token: string | undefined): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function fetchGamesCatalog(): Promise<GameCatalogResponse> {
  const res = await fetch(`${API_BASE}/games`)
  if (!res.ok) return throwApiError(res)
  return res.json()
}

export async function startPlayerGuessGame(
  anonSessionId: string | null,
  token: string | undefined
): Promise<PlayerGuessSessionResponse> {
  const res = await fetch(`${API_BASE}/games/player-guess/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ session_id: anonSessionId }),
  })
  if (!res.ok) return throwApiError(res)
  return res.json()
}

export async function fetchPlayerGuessSession(
  anonSessionId: string | null,
  token: string | undefined
): Promise<PlayerGuessSessionResponse> {
  const params = new URLSearchParams()
  if (anonSessionId) params.set('session_id', anonSessionId)
  const res = await fetch(`${API_BASE}/games/player-guess/session?${params.toString()}`, {
    headers: authHeaders(token),
  })
  if (!res.ok) return throwApiError(res)
  return res.json()
}

export async function applyPlayerGuessHint(
  gameSessionId: number,
  hintType: PlayerGuessHintType,
  anonSessionId: string | null,
  token: string | undefined
): Promise<PlayerGuessSessionResponse> {
  const params = new URLSearchParams()
  if (anonSessionId) params.set('session_id', anonSessionId)
  const res = await fetch(`${API_BASE}/games/player-guess/${gameSessionId}/hint?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ hint_type: hintType }),
  })
  if (!res.ok) return throwApiError(res)
  return res.json()
}

export async function submitPlayerGuess(
  gameSessionId: number,
  playerId: number,
  anonSessionId: string | null,
  token: string | undefined
): Promise<PlayerGuessSessionResponse> {
  const params = new URLSearchParams()
  if (anonSessionId) params.set('session_id', anonSessionId)
  const res = await fetch(`${API_BASE}/games/player-guess/${gameSessionId}/guess?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ player_id: playerId }),
  })
  if (!res.ok) return throwApiError(res)
  return res.json()
}

export async function skipPlayerGuessRound(
  gameSessionId: number,
  anonSessionId: string | null,
  token: string | undefined
): Promise<PlayerGuessSessionResponse> {
  const params = new URLSearchParams()
  if (anonSessionId) params.set('session_id', anonSessionId)
  const res = await fetch(`${API_BASE}/games/player-guess/${gameSessionId}/skip?${params.toString()}`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  if (!res.ok) return throwApiError(res)
  return res.json()
}

export async function endPlayerGuessGame(
  gameSessionId: number,
  anonSessionId: string | null,
  token: string | undefined
): Promise<PlayerGuessSessionResponse> {
  const params = new URLSearchParams()
  if (anonSessionId) params.set('session_id', anonSessionId)
  const res = await fetch(`${API_BASE}/games/player-guess/${gameSessionId}/end?${params.toString()}`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  if (!res.ok) return throwApiError(res)
  return res.json()
}
